import { ThemeProvider } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import clsx from 'clsx';
import { memo } from 'react';
import { useFuseLayoutSettings } from '@fuse/core/FuseLayout/FuseLayout';
import { useToolbarTheme } from '@fuse/core/FuseSettings/hooks/fuseThemeHooks';
import NavbarToggleButton from '@/components/theme-layouts/components/navbar/NavbarToggleButton';
import { selectFuseNavbar } from '@/components/theme-layouts/components/navbar/navbarSlice';
import { useAppSelector } from '@/store/hooks';
import LightDarkModeToggle from '@/components/LightDarkModeToggle';
import FullScreenToggle from '../../components/FullScreenToggle';
import HelpButton from '../../components/HelpButton';
import NavigationShortcuts from '../../components/navigation/NavigationShortcuts';
import ApplicationNavigation from '../../components/navigation/ApplicationNavigation';
import { Layout1ConfigDefaultsType } from '@/components/theme-layouts/layout1/Layout1Config';
import useThemeMediaQuery from '../../../../@fuse/hooks/useThemeMediaQuery';

type ToolbarLayout1Props = {
  className?: string;
};

/**
 * The toolbar layout 1.
 */
function ToolbarLayout1(props: ToolbarLayout1Props) {
  const { className } = props;

  const settings = useFuseLayoutSettings();
  const config = settings.config as Layout1ConfigDefaultsType;
  const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down('lg'));

  const navbar = useAppSelector(selectFuseNavbar);
  const toolbarTheme = useToolbarTheme();

  return (
    <ThemeProvider theme={toolbarTheme}>
      <AppBar
        id="fuse-toolbar"
        className={clsx('relative z-20 flex border-b', className)}
        color="default"
        sx={(theme) => ({
          backgroundColor: toolbarTheme.palette.background.default,
          ...theme.applyStyles('light', {
            backgroundColor: toolbarTheme.palette.background.paper
          })
        })}
        position="static"
        elevation={0}
      >
        <Toolbar className="min-h-48 p-0 md:min-h-64">
          <div className="flex min-w-0 flex-1 items-center px-8 md:px-16 space-x-8">
            {config.navbar.display && config.navbar.position === 'left' && (
              <>
                {!isMobile && (
                  <>
                    {(config.navbar.style === 'style-3' || config.navbar.style === 'style-3-dense') && (
                      <NavbarToggleButton className="mx-0 h-40 w-40 p-0" />
                    )}

                    {config.navbar.style === 'style-1' && !navbar.open && (
                      <NavbarToggleButton className="mx-0 h-40 w-40 p-0" />
                    )}
                  </>
                )}

                {isMobile && <NavbarToggleButton className="mx-0 h-40 w-40 p-0 sm:mx-8" />}
              </>
            )}

            {!isMobile && <NavigationShortcuts />}
            <ApplicationNavigation />
          </div>

          <div className="flex items-center overflow-x-auto px-8 md:px-16 space-x-6">
            <HelpButton />
            <FullScreenToggle />
            <LightDarkModeToggle />
          </div>

          {config.navbar.display && config.navbar.position === 'right' && (
            <>
              {!isMobile && (
                <>
                  {(config.navbar.style === 'style-3' || config.navbar.style === 'style-3-dense') && (
                    <NavbarToggleButton className="mx-0 h-40 w-40 p-0" />
                  )}

                  {config.navbar.style === 'style-1' && !navbar.open && (
                    <NavbarToggleButton className="mx-0 h-40 w-40 p-0" />
                  )}
                </>
              )}

              {isMobile && <NavbarToggleButton className="mx-0 h-40 w-40 p-0 sm:mx-8" />}
            </>
          )}
        </Toolbar>
      </AppBar>
    </ThemeProvider>
  );
}

export default memo(ToolbarLayout1);
